<script setup lang="ts">
import { ref } from "vue";
import BestelSidebarInfo from "./BestelSidebarInfo.vue";
import BestelSidebarKosten from "./BestelSidebarKosten.vue";
import BestelSidebarMobile from "./BestelSidebarMobile.vue";
import { useSportactiviteitBestelStore } from "@/stores/sportactiviteit-bestel.store";
import { SportactiviteitKosten } from "@/repositories/bestel.repository.js";

const store = useSportactiviteitBestelStore();

defineProps<{
    activiteit: any;
    aanbieder: any;
    locatie: any;
    lessen: any[] | null;
    boeking: any;
    alleKosten: SportactiviteitKosten | null;
    mobileTotalPrice: string;
    sidebarTitle?: string;
    sidebarItems?: { icon: string | null; text: string }[];
    isLoading?: boolean;
    showMobileSidebar?: boolean;
}>();

const emit = defineEmits<{
    (e: "open-lessons-overview"): void;
}>();

const mobileCartOpen = ref(false);
</script>

<template>
    <div class="row">
        <div class="col-xl-7">
            <div v-if="showMobileSidebar" class="mobile-cart-box d-md-none mb-3">
                <BestelSidebarMobile
                    :activiteit="activiteit"
                    :aanbieder="aanbieder"
                    :locatie="locatie"
                    :lessen="lessen"
                    :details-open="mobileCartOpen"
                    @open-lessons-overview="emit('open-lessons-overview')"
                />
            </div>

            <div v-if="store.initError" class="alert alert-danger mb-4" role="alert">
                {{ store.initError }}
            </div>
            <div v-if="!store.isInschrijvingPeriodeActief" class="alert alert-warning mb-4" role="alert">
                Inschrijven is momenteel niet mogelijk. De inschrijfperiode is gesloten.
            </div>

            <slot></slot>
        </div>

        <aside class="col-xl-5">
            <div class="shoppingcart" :class="{ open: mobileCartOpen }">
                <div class="shoppingcart-loader-wrapper">
                    <div v-if="isLoading" class="shoppingcart-loader" aria-hidden="true">
                        <div class="lds-ring"><div></div><div></div><div></div><div></div></div>
                    </div>
                    <div class="payment-cart-desktop">
                        <div class="payment-cart-info-box" :class="{ 'border-bottom-0': activiteit?.Bekostigingstype === 1 }">
                            <BestelSidebarInfo
                                :activiteit="activiteit"
                                :aanbieder="aanbieder"
                                :locatie="locatie"
                                :lessen="lessen"
                                @open-lessons-overview="emit('open-lessons-overview')"
                            />
                            <BestelSidebarKosten
                                :activiteit="activiteit"
                                :boeking="boeking"
                                :alle-kosten="alleKosten"
                            />
                        </div>
                        <div class="bg-gray-200 p-2 border-gray-400 border-top-0" v-if="activiteit?.Bekostigingstype === 1">
                            <span v-if="store.remainingSpaarpunten < 0" class="text-danger">
                                Je hebt niet genoeg credits om voor zoveel personen te boeken.
                            </span>
                            <span v-else>
                                Je hebt na het boeken van deze activiteit nog {{ store.remainingSpaarpunten }} credits over.
                            </span>
                        </div>
                        <div v-if="sidebarTitle || (sidebarItems && sidebarItems.length)" class="welcome-to-block">
                            <span v-if="sidebarTitle" class="d-block mb-1 cart-link-list-title">{{ sidebarTitle }}</span>
                            <ul v-if="sidebarItems && sidebarItems.length" class="welcome-to-block-list">
                                <li v-for="(item, idx) in sidebarItems" :key="idx" class="welcome-to-block-list-item">
                                    <div class="welcome-to-block-list-item-icon-box">
                                        <span :class="'icon ' + (item.icon || 'icon-check2')"></span>
                                    </div>
                                    <div class="welcome-to-block-list-item-text" v-html="item.text"></div>
                                </li>
                            </ul>
                        </div>
                    </div>
                    <div class="payment-cart-mobile">
                        <div class="payment-cart-mobile-container ">
                            <div class="payment-cart-mobile-container-left">
                                <p class="payment-cart-mobile-price">{{ mobileTotalPrice }}</p>
                                <div class="payment-cart-mobile-chevron-box" @click="mobileCartOpen = !mobileCartOpen">
                                    <div class="payment-cart-mobile-chevron-circle">
                                        <span :class="mobileCartOpen ? 'icon-chevron--down' : 'icon-chevron--up'" aria-hidden="true"></span>
                                    </div>
                                    <p class="payment-cart-mobile-details">Details</p>
                                </div>
                            </div>
                        </div>
                        <slot name="action"></slot>
                    </div>
                </div>
            </div>
        </aside>
    </div>
</template>
